// pages/fit/FitDashboard.js - VERSION CORRIGÉE
import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebaseConfig";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  deleteDoc,
  limit,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

// 🎯 FONCTION POUR IDENTIFIER LE RÔLE UTILISATEUR
const getUserRole = (order) => {
  if (!order) return null;
  
  // Chercher dans destinataire_type
  const destinataireType = order.destinataire_type?.toLowerCase();
  
  if (destinataireType === "carrossier") return "CARROSSIER";
  if (destinataireType === "revendeur" || destinataireType === "controleur") return "REVENDEUR";
  if (destinataireType === "utilisateur") return "UTILISATEUR";
  
  // Fallback - analyser l'email ou d'autres indices
  const email = order.revendeurEmail?.toLowerCase() || "";
  if (email.includes("carrossier")) return "CARROSSIER";
  if (email.includes("revendeur") || email.includes("controleur")) return "REVENDEUR";
  
  // Par défaut = UTILISATEUR
  return "UTILISATEUR";
};

// 🎯 FONCTION POUR OBTENIR LES DOCUMENTS AUTORISÉS PAR RÔLE
const getAuthorizedDocuments = (role) => {
  switch(role) {
    case "CARROSSIER":
      return {
        declarationMontage: true,
        miseEnService: true, 
        declarationCE: true,
        noticeInstruction: true,
        controlePeriodique: false
      };
      
    case "REVENDEUR":
      return {
        declarationMontage: false,
        miseEnService: false,
        declarationCE: false, 
        noticeInstruction: true,
        controlePeriodique: true
      };
      
    case "UTILISATEUR":
      return {
        declarationMontage: false,
        miseEnService: false,
        declarationCE: true,
        noticeInstruction: true,
        controlePeriodique: true
      };
      
    default:
      return {
        declarationMontage: false,
        miseEnService: false,
        declarationCE: false,
        noticeInstruction: false,
        controlePeriodique: false
      };
  }
};

// ✅ FONCTIONS UTILITAIRES CORRIGÉES
const getControlePeriodiqueDate = (order) => {
  if (!order.createdAt) return null;
  
  let createdDate;
  if (order.createdAt.toDate && typeof order.createdAt.toDate === 'function') {
    createdDate = order.createdAt.toDate();
  } else if (order.createdAt instanceof Date) {
    createdDate = order.createdAt;
  } else {
    createdDate = new Date(order.createdAt);
  }
  
  if (isNaN(createdDate.getTime())) return null;
  
  // Ajouter 6 mois à la date de création
  const controleDate = new Date(createdDate);
  controleDate.setMonth(controleDate.getMonth() + 6);
  
  return controleDate;
};

const getDaysRemaining = (order) => {
  const controleDate = getControlePeriodiqueDate(order);
  if (!controleDate) return null;
  
  const now = new Date();
  const diffTime = controleDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

// 🔥 FONCTION CORRIGÉE POUR GÉNÉRATION ID COHÉRENTE
const generateUnitDeclarationId = (product, productIndex) => {
  const baseName = product.name?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'product';
  const productId = product.productId || product.uuid || product.id || `${baseName}_${productIndex}`;
  return productId;
};

const FitDashboard = () => {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedTimeframe, setSelectedTimeframe] = useState("7days");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const navigate = useNavigate();

  // Auth listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      console.log("🔄 Auth state changed:", u ? "connecté" : "déconnecté");
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  // Firestore listeners
  useEffect(() => {
    if (!user) {
      console.log("⏳ Utilisateur non connecté, en attente...");
      return;
    }

    console.log("✅ Utilisateur connecté, récupération des données...");

    try {
      const unsubscribeDossiers = onSnapshot(
        query(collection(db, "dossiers"), orderBy("createdAt", "desc")),
        (snapshot) => {
          console.log("📦 Dossiers reçus:", snapshot.docs.length);
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setOrders(data);
        },
        (error) => {
          console.error("❌ Erreur dossiers:", error);
        }
      );

      const unsubscribeNotifications = onSnapshot(
        query(collection(db, "notifications"), orderBy("createdAt", "desc"), limit(50)),
        (snapshot) => {
          console.log("🔔 Notifications reçues:", snapshot.docs.length);
          const fetched = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setNotifications(fetched);
        },
        (error) => {
          console.error("❌ Erreur notifications:", error);
        }
      );

      return () => {
        console.log("🧹 Nettoyage des listeners");
        unsubscribeDossiers();
        unsubscribeNotifications();
      };
    } catch (error) {
      console.error("❌ Erreur configuration listeners:", error);
    }
  }, [user]);

  // 🔥 FONCTION CORRIGÉE : Vérification déclarations multiples cohérente avec carrossier
  const getDeclarationMontageStatus = (order, product, productIndex) => {
    if (!order || !product) return { completed: 0, total: 0, declarations: [] };
    
    const quantity = parseInt(product.quantity) || 1;
    const baseProductId = generateUnitDeclarationId(product, productIndex);
    const declarations = [];
    
    console.log(`🔍 FIT - Vérification déclarations pour "${product.name}" (${quantity} unité${quantity > 1 ? 's' : ''})`);
    
    for (let unitIndex = 0; unitIndex < quantity; unitIndex++) {
      // 🔥 CORRECTION MAJEURE : Utiliser la même logique que le carrossier
      const declarationId = quantity > 1 ? `${baseProductId}_unit_${unitIndex + 1}` : baseProductId;
      
      // 🔥 CHERCHER TOUTES LES CLÉS POSSIBLES
      const statusKey = `${declarationId}_status`;
      const montageCompleteKey = `${declarationId}_montage_complete`; // 🔥 CLÉ PRINCIPALE
      const filledKey = `${declarationId}_filled`;
      const declarationKey = `declarationMontage_${declarationId}`;
      
      const isStatusComplete = order[statusKey] === "declaration_montage_complete";
      const isMontageComplete = !!order[montageCompleteKey]; // 🔥 NOUVELLE VÉRIFICATION
      const isFilled = !!order[filledKey];
      const hasDeclarationData = !!order[declarationKey];
      
      // 🔥 LOGIQUE CORRIGÉE : Considérer comme complète si montage_complete OU status correct
      const isComplete = isStatusComplete || isMontageComplete || hasDeclarationData;
      
      console.log(`📋 FIT - Unité ${unitIndex + 1} (${declarationId}):`, {
        isStatusComplete,
        isMontageComplete, // 🔥 NOUVEAU LOG
        isFilled,
        hasDeclarationData,
        isComplete
      });
      
      declarations.push({
        unitNumber: unitIndex + 1,
        declarationId,
        isComplete,
        isMontageComplete, // 🔥 NOUVEAU CHAMP
        isFilled,
        hasDeclarationData,
        statusKey,
        montageCompleteKey // 🔥 NOUVEAU CHAMP
      });
    }
    
    const completed = declarations.filter(d => d.isComplete).length;
    
    console.log(`📊 FIT - Résultat: ${completed}/${quantity} déclarations reçues`);
    
    return {
      completed,
      total: quantity,
      declarations,
      isAllComplete: completed === quantity,
      hasPartial: completed > 0 && completed < quantity,
      completionRate: Math.round((completed / quantity) * 100)
    };
  };

  // 🔥 FONCTION CORRIGÉE : Comptage déclarations selon le nouveau système
  const countDeclarationsCompleted = (order) => {
    if (!order.produits || !Array.isArray(order.produits)) return 0;
    
    // Ne compter les déclarations que pour les carrossiers
    const userRole = getUserRole(order);
    const authorizedDocs = getAuthorizedDocuments(userRole);
    
    if (!authorizedDocs.declarationMontage) return 0;
    
    return order.produits.reduce((totalCompleted, produit, index) => {
      const status = getDeclarationMontageStatus(order, produit, index);
      return totalCompleted + status.completed;
    }, 0);
  };

  // 🔥 FONCTION CORRIGÉE : Comptage total déclarations
  const countDeclarationsTotal = (order) => {
    if (!order.produits || !Array.isArray(order.produits)) return 0;
    
    const userRole = getUserRole(order);
    const authorizedDocs = getAuthorizedDocuments(userRole);
    
    if (!authorizedDocs.declarationMontage) return 0;
    
    return order.produits.reduce((totalExpected, produit) => {
      const quantity = parseInt(produit.quantity) || 1;
      return totalExpected + quantity;
    }, 0);
  };

  // 🔥 FONCTION POUR COMPTER LES FICHIERS JOINTS
  const countAttachedFiles = (order) => {
    if (!order.produits || !Array.isArray(order.produits)) return 0;
    
    return order.produits.reduce((totalFiles, produit) => {
      // Compter les fichiers dans fileCount (nouvelle propriété)
      if (produit.fileCount && typeof produit.fileCount === 'number') {
        totalFiles += produit.fileCount;
      }
      // Fallback: compter les fichiers dans files array
      else if (produit.files && Array.isArray(produit.files)) {
        totalFiles += produit.files.length;
      }
      return totalFiles;
    }, 0);
  };

  // 🔥 FONCTION AMÉLIORÉE POUR FORMATER LES NOTIFICATIONS
  const formatNotificationMessage = (notif, relatedOrder = null) => {
    // Si on a l'ordre associé, enrichir le message
    if (relatedOrder && notif.dossierId) {
      const orderName = relatedOrder.orderName || "Dossier sans nom";
      const email = relatedOrder.revendeurEmail || "Email non défini";
      const destinataire = relatedOrder.destinataire_type || "Type non défini";
      
      // Analyser le type de notification depuis le message original
      const originalMessage = notif.message || "";
      
      if (originalMessage.includes("déclaration de montage")) {
        return {
          title: "📋 Déclaration de montage reçue",
          details: `Dossier: ${orderName}`,
          meta: `${destinataire} • ${email}`,
          originalMessage
        };
      }
      
      if (originalMessage.includes("contrôle périodique")) {
        return {
          title: "🔍 Contrôle périodique à planifier", 
          details: `Dossier: ${orderName}`,
          meta: `${destinataire} • ${email}`,
          originalMessage
        };
      }
      
      if (originalMessage.includes("formulaire")) {
        return {
          title: "📝 Formulaire CE complété",
          details: `Dossier: ${orderName}`,
          meta: `${destinataire} • ${email}`,
          originalMessage
        };
      }
      
      // Par défaut, utiliser le message original avec les infos du dossier
      return {
        title: originalMessage,
        details: `Dossier: ${orderName}`,
        meta: `${destinataire} • ${email}`,
        originalMessage
      };
    }
    
    // Fallback - message original uniquement
    return {
      title: notif.message || "Notification",
      details: notif.dossierId ? `Dossier: ${notif.dossierId}` : "",
      meta: "",
      originalMessage: notif.message || ""
    };
  };

  // Fonctions utilitaires
  const formatDate = (dateInput) => {
    if (!dateInput) return 'Non définie';
    
    let dateObj;
    if (dateInput.toDate && typeof dateInput.toDate === 'function') {
      dateObj = dateInput.toDate();
    } else if (dateInput.seconds) {
      dateObj = new Date(dateInput.seconds * 1000);
    } else if (dateInput instanceof Date) {
      dateObj = dateInput;
    } else {
      dateObj = new Date(dateInput);
    }
    
    if (isNaN(dateObj.getTime())) return 'Non définie';
    
    return dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatRelativeTime = (dateInput) => {
    if (!dateInput) return 'Non définie';
    
    let dateObj;
    if (dateInput.toDate && typeof dateInput.toDate === 'function') {
      dateObj = dateInput.toDate();
    } else if (dateInput.seconds) {
      dateObj = new Date(dateInput.seconds * 1000);
    } else if (dateInput instanceof Date) {
      dateObj = dateInput;
    } else {
      dateObj = new Date(dateInput);
    }
    
    if (isNaN(dateObj.getTime())) return 'Non définie';
    
    const now = new Date();
    const diffTime = now - dateObj;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffMinutes < 60) return `Il y a ${diffMinutes}min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return formatDate(dateInput);
  };

  // ✅ FONCTION CORRIGÉE - Utilise la nouvelle logique avec rôles
  const getControleStatus = (order) => {
    const userRole = getUserRole(order);
    const authorizedDocs = getAuthorizedDocuments(userRole);
    
    // Si le rôle n'autorise pas les contrôles périodiques, pas de statut
    if (!authorizedDocs.controlePeriodique) return 'not_applicable';
    
    const controleDate = getControlePeriodiqueDate(order);
    if (!controleDate) return 'no_date';
    
    const daysRemaining = getDaysRemaining(order);
    
    if (daysRemaining === null) return 'no_date';
    if (daysRemaining < 0) return 'overdue';
    if (daysRemaining === 0) return 'today';
    if (daysRemaining <= 7) return 'urgent';
    if (daysRemaining <= 30) return 'soon';
    return 'pending';
  };

  // ✅ FONCTION CORRIGÉE - Calculs et filtres avancés avec rôles
  const getControlesByTimeframe = () => {
    const now = new Date();
    let cutoffDate;
    
    switch (selectedTimeframe) {
      case '7days':
        cutoffDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        cutoffDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        break;
      case '90days':
        cutoffDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        cutoffDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    }

    return orders
      .filter(order => {
        const userRole = getUserRole(order);
        const authorizedDocs = getAuthorizedDocuments(userRole);
        
        // 🔥 CORRECTION - Filtrer seulement les ordres qui nécessitent un contrôle périodique
        // ET qui ont une date de création valide
        return authorizedDocs.controlePeriodique && order.createdAt && getControlePeriodiqueDate(order);
      })
      .map(order => ({
        ...order,
        daysRemaining: getDaysRemaining(order),
        status: getControleStatus(order),
        formattedDate: formatDate(getControlePeriodiqueDate(order)),
        userRole: getUserRole(order) // Ajouter le rôle pour debug
      }))
      .filter(order => {
        const controleDate = getControlePeriodiqueDate(order);
        return controleDate && controleDate <= cutoffDate;
      })
      .sort((a, b) => {
        const priority = { overdue: 0, today: 1, urgent: 2, soon: 3, pending: 4 };
        if (priority[a.status] !== priority[b.status]) {
          return priority[a.status] - priority[b.status];
        }
        return (a.daysRemaining || 0) - (b.daysRemaining || 0);
      });
  };

  const getStats = () => {
    const controles = getControlesByTimeframe();
    
    // 🔥 NOUVEAU CALCUL AVEC DÉCLARATIONS DE MONTAGE ET RÔLES
    const totalDocs = orders.reduce((acc, o) => acc + countDocumentsTotal(o), 0);
    const completedDocs = orders.reduce((acc, o) => acc + countDocumentsCompletes(o), 0);
    
    // 🔥 CORRECTION MAJEURE : Compter les déclarations avec la nouvelle logique
    const carrossierOrders = orders.filter(o => getUserRole(o) === "CARROSSIER");
    const totalDeclarations = carrossierOrders.reduce((acc, o) => acc + countDeclarationsTotal(o), 0);
    const completedDeclarations = carrossierOrders.reduce((acc, o) => acc + countDeclarationsCompleted(o), 0);
    
    console.log("📊 FIT Stats globales:", {
      totalDeclarations,
      completedDeclarations,
      carrossierOrdersCount: carrossierOrders.length
    });
    
    return {
      totalOrders: orders.length,
      totalDocs,
      completedDocs,
      pendingDocs: totalDocs - completedDocs,
      completionRate: totalDocs > 0 ? Math.round((completedDocs / totalDocs) * 100) : 0,
      totalDeclarations,
      completedDeclarations,
      declarationRate: totalDeclarations > 0 ? Math.round((completedDeclarations / totalDeclarations) * 100) : 0,
      controles: {
        total: controles.length,
        overdue: controles.filter(c => c.status === 'overdue').length,
        today: controles.filter(c => c.status === 'today').length,
        urgent: controles.filter(c => c.status === 'urgent').length,
        soon: controles.filter(c => c.status === 'soon').length,
      }
    };
  };

  const countDocumentsCompletes = (order) => {
    const userRole = getUserRole(order);
    const authorizedDocs = getAuthorizedDocuments(userRole);
    
    let count = 0;
    if (order.produits && Array.isArray(order.produits)) {
      count += order.produits.reduce((acc, produit, index) => {
        let c = 0;
        
        // CARROSSIER: Formulaire CE + Déclaration montage + Notice
        if (userRole === "CARROSSIER") {
          if (produit.filled || produit.formulaire || produit.formulaireData) c += 1; // Mise en service
          
          // 🔥 CORRECTION : Utiliser la nouvelle logique de déclaration
          const declarationStatus = getDeclarationMontageStatus(order, produit, index);
          c += declarationStatus.completed; // Ajouter le nombre de déclarations complètes
          
          // Notice toujours disponible = +1
          c += 1;
        }
        
        // REVENDEUR: Contrôle périodique + Notice
        else if (userRole === "REVENDEUR") {
          // Pour l'instant, contrôle périodique = en attente (+0)
          // Notice toujours disponible = +1
          c += 1;
        }
        
        // UTILISATEUR: Déclaration CE + Notice + Contrôle périodique
        else if (userRole === "UTILISATEUR") {
          if (produit.filled || produit.formulaire || produit.formulaireData) c += 1; // Déclaration CE
          // Notice toujours disponible = +1
          c += 1;
          // Pour l'instant, contrôle périodique = en attente (+0)
        }
        
        return acc + c;
      }, 0);
    }
    return count;
  };

  const countDocumentsTotal = (order) => {
    if (!order.produits || !Array.isArray(order.produits)) return 0;
    
    const userRole = getUserRole(order);
    
    let totalDocs = 0;
    
    order.produits.forEach((produit) => {
      const quantity = parseInt(produit.quantity) || 1;
      
      // CARROSSIER: 2 + quantity documents (Mise en service + Notice + Déclarations de montage par unité)
      if (userRole === "CARROSSIER") {
        totalDocs += 2 + quantity; // Mise en service + Notice + (1 déclaration par unité)
      }
      // REVENDEUR: 2 documents par produit
      else if (userRole === "REVENDEUR") {
        totalDocs += 2; // Contrôle périodique + Notice
      }
      // UTILISATEUR: 3 documents par produit
      else if (userRole === "UTILISATEUR") {
        totalDocs += 3; // Déclaration CE + Notice + Contrôle périodique
      }
    });
    
    return totalDocs;
  };

  // Reste du code inchangé...
  // (fonctions de filtrage, gestionnaires d'événements, etc.)

  // Filtrage intelligent avec recherche étendue
  const getFilteredOrders = () => {
    let filtered = orders.filter((order) => {
      const term = searchTerm.toLowerCase();
      
      // 🔥 RECHERCHE ÉTENDUE avec numéros d'affaire et de commande
      const matchesSearch = 
        order.orderName?.toLowerCase().includes(term) ||
        order.revendeurEmail?.toLowerCase().includes(term) ||
        order.clientAffaireNumber?.toLowerCase().includes(term) ||
        order.clientOrderNumber?.toLowerCase().includes(term) ||
        order.destinataire_type?.toLowerCase().includes(term) ||
        order.produits?.some(p => p.name?.toLowerCase().includes(term));
      
      if (!matchesSearch) return false;

      switch (filterStatus) {
        case "pending":
          return countDocumentsCompletes(order) < countDocumentsTotal(order);
        case "completed":
          return countDocumentsCompletes(order) === countDocumentsTotal(order);
        case "needs_attention":
          return getControleStatus(order) === 'overdue' || getControleStatus(order) === 'today';
        default:
          return true;
      }
    });

    // Pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    return {
      items: filtered.slice(startIndex, endIndex),
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / itemsPerPage)
    };
  };

  // Gestionnaires d'événements
  const handleDeleteDossier = async (dossierId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce dossier ?")) return;

    try {
      await deleteDoc(doc(db, "dossiers", dossierId));
      console.log("✅ Dossier supprimé:", dossierId);
    } catch (error) {
      console.error("❌ Erreur suppression :", error);
    }
  };

  const handleDeleteNotification = async (notifId) => {
    try {
      await deleteDoc(doc(db, "notifications", notifId));
      console.log("✅ Notification supprimée:", notifId);
    } catch (error) {
      console.error("❌ Erreur suppression notification :", error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const stats = getStats();
  const filteredOrdersData = getFilteredOrders();
  const controles = getControlesByTimeframe();
  const recentNotifications = notifications.slice(0, 5);

  // Le reste du JSX reste identique à la version originale...
  // (je vais continuer avec la partie affichage dans le prochain artifact)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      
      {/* Header moderne */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Tableau de bord FIT
              </h1>
              <p className="text-gray-600 text-sm">
                Gestion des dossiers CE et contrôles périodiques
              </p>
            </div>
            
            {/* Notifications modernes AMÉLIORÉES */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-3 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              >
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notifications.length > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {notifications.length > 9 ? '9+' : notifications.length}
                    </span>
                  </div>
                )}
              </button>
              
              {/* Panel notifications moderne AMÉLIORÉ */}
              {showNotifications && (
                <div className="absolute right-0 top-14 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                      <button
                        onClick={() => setShowNotifications(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {recentNotifications.length === 0 ? (
                      <div className="p-6 text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                        </div>
                        <p className="text-gray-500 text-sm">Aucune notification</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {recentNotifications.map((notif) => {
                          // 🔥 AMÉLIORATION - Trouver l'ordre associé pour enrichir la notification
                          const relatedOrder = notif.dossierId ? orders.find(o => o.id === notif.dossierId) : null;
                          const formattedNotif = formatNotificationMessage(notif, relatedOrder);
                          
                          return (
                            <div key={notif.id} className="p-4 hover:bg-gray-50 group">
                              <div className="flex gap-3">
                                <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                                <div className="flex-1">
                                  <div 
                                    className="cursor-pointer"
                                    onClick={() => {
                                      if (notif.dossierId) {
                                        navigate(`/fit/orders/${notif.dossierId}`);
                                        setShowNotifications(false);
                                      }
                                    }}
                                  >
                                    {/* 🔥 TITRE ENRICHI */}
                                    <p className="text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-2">
                                      {formattedNotif.title}
                                    </p>
                                    
                                    {/* 🔥 DÉTAILS DU DOSSIER */}
                                    {formattedNotif.details && (
                                      <p className="text-xs text-gray-600 mt-1 font-medium">
                                        {formattedNotif.details}
                                      </p>
                                    )}
                                    
                                    {/* 🔥 MÉTA-INFORMATIONS (Email + Type) */}
                                    {formattedNotif.meta && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        {formattedNotif.meta}
                                      </p>
                                    )}
                                    
                                    {/* 🔥 TIMESTAMP */}
                                    <p className="text-xs text-gray-400 mt-1">
                                      {formatRelativeTime(notif.createdAt)}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeleteNotification(notif.id)}
                                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-all"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Stats modernes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Dossiers totaux</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Déclarations montage</p>
                <p className="text-2xl font-bold text-green-600">{stats.declarationRate}%</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mt-3">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${stats.declarationRate}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {stats.completedDeclarations}/{stats.totalDeclarations} reçues
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Contrôles urgents</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.controles.overdue + stats.controles.today + stats.controles.urgent}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {stats.controles.overdue} en retard • {stats.controles.today} aujourd'hui • {stats.controles.urgent} cette semaine
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taux de complétion</p>
                <p className="text-2xl font-bold text-orange-600">{stats.completionRate}%</p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mt-3">
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${stats.completionRate}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          
          {/* Liste des dossiers moderne */}
          <div className="xl:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Dossiers CE
                  </h2>
                  
                  <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Rechercher dossier, email, N° affaire..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="all">Tous</option>
                      <option value="pending">En cours</option>
                      <option value="completed">Terminés</option>
                      <option value="needs_attention">Attention requise</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="overflow-hidden">
                {filteredOrdersData.items.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun dossier trouvé</h3>
                    <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
                  </div>
                ) : (
                  <>
                    <div className="divide-y divide-gray-100">
                      {filteredOrdersData.items.map((order) => {
                        const completion = countDocumentsCompletes(order);
                        const total = countDocumentsTotal(order);
                        const percentage = total > 0 ? Math.round((completion / total) * 100) : 0;
                        const controleStatus = getControleStatus(order);
                        const userRole = getUserRole(order);
                        const authorizedDocs = getAuthorizedDocuments(userRole);
                        const attachedFiles = countAttachedFiles(order);
                        
                        // 🔥 NOUVELLE LOGIQUE : Calculer les déclarations de montage correctement
                        let totalDeclarations = 0;
                        let completedDeclarations = 0;
                        
                        if (authorizedDocs.declarationMontage && order.produits) {
                          order.produits.forEach((produit, index) => {
                            const declarationStatus = getDeclarationMontageStatus(order, produit, index);
                            totalDeclarations += declarationStatus.total;
                            completedDeclarations += declarationStatus.completed;
                          });
                        }
                        
                        return (
                          <div
                            key={order.id}
                            className="p-6 hover:bg-gray-50 transition-colors cursor-pointer group"
                            onClick={() => navigate(`/fit/orders/${order.id}`)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="text-sm font-semibold text-gray-900 truncate">
                                    {order.orderName}
                                  </h3>
                                  
                                  {/* Badge de rôle */}
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    userRole === 'CARROSSIER' ? 'bg-blue-100 text-blue-800' :
                                    userRole === 'REVENDEUR' ? 'bg-green-100 text-green-800' :
                                    userRole === 'UTILISATEUR' ? 'bg-purple-100 text-purple-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {userRole || 'Non défini'}
                                  </span>
                                  
                                  {/* 🔥 NOUVEAU - Badge fichiers joints */}
                                  {attachedFiles > 0 && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                      📎 {attachedFiles} fichier{attachedFiles > 1 ? 's' : ''}
                                    </span>
                                  )}
                                  
                                  {controleStatus === 'overdue' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      En retard
                                    </span>
                                  )}
                                  {controleStatus === 'urgent' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      Urgent
                                    </span>
                                  )}
                                </div>
                                
                                {/* 🔥 AMÉLIORÉ - Informations étendues */}
                                <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 mb-3">
                                  <div className="space-y-1">
                                    <div><span className="font-medium">Email:</span> {order.revendeurEmail || "Non défini"}</div>
                                    <div><span className="font-medium">Destinataire:</span> {order.destinataire_type || "Non défini"}</div>
                                    {order.clientAffaireNumber && (
                                      <div><span className="font-medium">N° Affaire:</span> {order.clientAffaireNumber}</div>
                                    )}
                                  </div>
                                  <div className="space-y-1">
                                    <div><span className="font-medium">Créé le:</span> {formatDate(order.createdAt)}</div>
                                    {order.deliveryDate && (
                                      <div><span className="font-medium">Livraison:</span> {formatDate(order.deliveryDate)}</div>
                                    )}
                                    {order.clientOrderNumber && (
                                      <div><span className="font-medium">N° Commande:</span> {order.clientOrderNumber}</div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* 🔥 AMÉLIORÉ - Date contrôle périodique */}
                                {getControlePeriodiqueDate(order) && (
                                  <div className="text-xs text-gray-500 mb-3">
                                    <span className="font-medium">Contrôle périodique:</span> {formatDate(getControlePeriodiqueDate(order))}
                                    {getDaysRemaining(order) !== null && (
                                      <span className={`ml-2 font-medium ${
                                        getDaysRemaining(order) < 0 ? 'text-red-600' :
                                        getDaysRemaining(order) <= 7 ? 'text-orange-600' :
                                        'text-green-600'
                                      }`}>
                                        ({getDaysRemaining(order) < 0 ? 
                                          `${Math.abs(getDaysRemaining(order))} jour${Math.abs(getDaysRemaining(order)) > 1 ? 's' : ''} de retard` :
                                          `dans ${getDaysRemaining(order)} jour${getDaysRemaining(order) > 1 ? 's' : ''}`
                                        })
                                      </span>
                                    )}
                                  </div>
                                )}
                                
                                {/* 🔥 NOUVEAU - Déclarations de montage pour carrossiers avec nouveau calcul */}
                                {totalDeclarations > 0 && (
                                  <div className="text-xs text-gray-500 mb-3">
                                    <span className={`font-medium ${
                                      completedDeclarations === totalDeclarations ? 'text-green-600' : 
                                      completedDeclarations > 0 ? 'text-orange-600' : 'text-red-600'
                                    }`}>
                                      📋 Déclarations montage: {completedDeclarations}/{totalDeclarations} reçues
                                      {totalDeclarations > 0 && (
                                        <span className="ml-1">
                                          ({Math.round((completedDeclarations / totalDeclarations) * 100)}%)
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                )}
                                
                                <div className="flex items-center gap-3">
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between text-xs mb-1">
                                      <span className="text-gray-600">Progression</span>
                                      <span className="font-medium text-gray-900">{completion}/{total}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                      <div 
                                        className={`h-2 rounded-full transition-all duration-500 ${
                                          percentage === 100 ? 'bg-green-500' : 
                                          percentage >= 50 ? 'bg-blue-500' : 'bg-orange-500'
                                        }`}
                                        style={{ width: `${percentage}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                  <span className={`text-sm font-medium ${
                                    percentage === 100 ? 'text-green-600' : 
                                    percentage >= 50 ? 'text-blue-600' : 'text-orange-600'
                                  }`}>
                                    {percentage}%
                                  </span>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2 ml-4">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/fit/orders/${order.id}`);
                                  }}
                                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Voir le dossier"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteDossier(order.id);
                                  }}
                                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Supprimer"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Pagination moderne */}
                    {filteredOrdersData.totalPages > 1 && (
                      <div className="p-6 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-600">
                            Affichage de {((currentPage - 1) * itemsPerPage) + 1} à {Math.min(currentPage * itemsPerPage, filteredOrdersData.total)} sur {filteredOrdersData.total} dossiers
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Précédent
                            </button>
                            
                            <div className="flex items-center gap-1">
                              {[...Array(Math.min(5, filteredOrdersData.totalPages))].map((_, i) => {
                                const pageNum = i + 1;
                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    className={`w-8 h-8 text-sm rounded-lg transition-colors ${
                                      currentPage === pageNum
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                            </div>
                            
                            <button
                              onClick={() => setCurrentPage(Math.min(filteredOrdersData.totalPages, currentPage + 1))}
                              disabled={currentPage === filteredOrdersData.totalPages}
                              className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              Suivant
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar moderne avec contrôles périodiques */}
          <div className="space-y-6">
            
            {/* Contrôles périodiques prioritaires */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Contrôles périodiques
                  </h3>
                  <select
                    value={selectedTimeframe}
                    onChange={(e) => setSelectedTimeframe(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="7days">7 prochains jours</option>
                    <option value="30days">30 prochains jours</option>
                    <option value="90days">90 prochains jours</option>
                  </select>
                </div>
                
                {/* Mini stats */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-red-50 rounded-lg p-3">
                    <div className="text-lg font-bold text-red-600">
                      {stats.controles.overdue + stats.controles.today}
                    </div>
                    <div className="text-xs text-red-600">Urgent</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3">
                    <div className="text-lg font-bold text-orange-600">
                      {stats.controles.urgent + stats.controles.soon}
                    </div>
                    <div className="text-xs text-orange-600">À venir</div>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {controles.length === 0 ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600">Aucun contrôle dans la période sélectionnée</p>
                    <p className="text-xs text-gray-500 mt-1">Les contrôles apparaîtront 6 mois après la création des dossiers</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {controles.slice(0, 10).map(order => {
                      const getStatusConfig = (status, days) => {
                        switch (status) {
                          case 'overdue':
                            return {
                              bgColor: 'bg-red-50 border-red-200',
                              textColor: 'text-red-700',
                              badgeColor: 'bg-red-100 text-red-700',
                              text: `${Math.abs(days)} jour${Math.abs(days) > 1 ? 's' : ''} de retard`,
                              icon: '🔴'
                            };
                          case 'today':
                            return {
                              bgColor: 'bg-yellow-50 border-yellow-200',
                              textColor: 'text-yellow-700',
                              badgeColor: 'bg-yellow-100 text-yellow-700',
                              text: "Aujourd'hui",
                              icon: '🟡'
                            };
                          case 'urgent':
                            return {
                              bgColor: 'bg-orange-50 border-orange-200',
                              textColor: 'text-orange-700',
                              badgeColor: 'bg-orange-100 text-orange-700',
                              text: `Dans ${days} jour${days > 1 ? 's' : ''}`,
                              icon: '🟠'
                            };
                          case 'soon':
                            return {
                              bgColor: 'bg-blue-50 border-blue-200',
                              textColor: 'text-blue-700',
                              badgeColor: 'bg-blue-100 text-blue-700',
                              text: `Dans ${days} jour${days > 1 ? 's' : ''}`,
                              icon: '🔵'
                            };
                          default:
                            return {
                              bgColor: 'bg-gray-50 border-gray-200',
                              textColor: 'text-gray-700',
                              badgeColor: 'bg-gray-100 text-gray-700',
                              text: `Dans ${days} jour${days > 1 ? 's' : ''}`,
                              icon: '⚪'
                            };
                        }
                      };

                      const config = getStatusConfig(order.status, order.daysRemaining);

                      return (
                        <div
                          key={order.id}
                          className={`p-4 rounded-lg border cursor-pointer hover:shadow-sm transition-all ${config.bgColor}`}
                          onClick={() => navigate(`/fit/orders/${order.id}`)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-sm text-gray-900 truncate pr-2">
                              {order.orderName}
                            </h4>
                            <span className="text-xs">{config.icon}</span>
                          </div>
                          
                          <div className="text-xs text-gray-600 mb-2 truncate">
                            {order.revendeurEmail || 'Email non défini'}
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              {order.formattedDate}
                            </span>
                            <span className={`text-xs font-medium px-2 py-1 rounded ${config.badgeColor}`}>
                              {config.text}
                            </span>
                          </div>
                          
                          {order.produits && order.produits.length > 0 && (
                            <div className="text-xs text-gray-500 mt-2">
                              {order.produits.length} produit{order.produits.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            
            {/* Actions rapides */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/fit/create-order')}
                  className="w-full flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <span className="font-medium text-blue-900">Créer un dossier</span>
                </button>
                
                <button
                  onClick={() => navigate('/fit/fit-create-account')}
                  className="w-full flex items-center gap-3 p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <span className="font-medium text-green-900">Créer un compte</span>
                </button>
                
                <button
                  onClick={() => navigate('/fit/manage-accounts')}
                  className="w-full flex items-center gap-3 p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-left"
                >
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-purple-900">Gérer les comptes</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FitDashboard;